import { useEffect, useRef, useState } from "react";
import { START_PAGE } from "./nav";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "./config";

const memberKey = (token) => `cm_${token}`;
const spentKey = (code) => `sso_ok_${code}`;

// A BARE axios instance, deliberately not the shared default.
//
// setupAxios() registers a global response interceptor that treats any 401 as
// "the learner's JWT expired" and hard-redirects to /login?expired=1. That is
// right everywhere else and wrong here: there is no session yet to expire, and
// when redeeming failed the redirect fired before this page could say why —
// the creator landed on a login screen reading "Your session expired." Axios
// interceptors are per-instance, so a fresh one is simply outside that rule.
const sso = axios.create();

/**
 * Landing page for the admin's "Sign in as instructor" link:
 *   /membership/:token/sso/:code
 *
 * The code is single-use and expires in a minute (flexiapi
 * AuthController::ssoLogin), so this redeems it immediately and never keeps it —
 * a reload lands on the "link already used" state rather than signing in twice.
 *
 * The session it stores is deliberately the SAME shape MembershipLogin writes,
 * so every screen downstream works without knowing how the member got here —
 * an admin preview is indistinguishable from the instructor's own sign-in.
 */
export default function MembershipSsoLogin() {
  const { token, code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  // StrictMode double-invokes effects in dev; without this the second run
  // redeems an already-spent code and reports a failure over a success.
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    (async () => {
      try {
        const res = await sso.post(`${config.COMMUNITY_API_URL}/membership-sso-login`, { code });
        const data = res.data || {};
        const mu = data.MembershipUser || {};
        if (!data.access_token || mu.id == null) {
          setError(data.message || "This sign-in link is no longer valid.");
          return;
        }
        localStorage.setItem(
          memberKey(token),
          JSON.stringify({
            community_member_id: mu.id,
            funnel_id: mu.funnel_id,
            name: mu.name || "",
            email: mu.email || "",
            img: mu.profile_img || null,
            points: mu.tot_points || 0,
            url_token: String(mu.id),
            auth_token: data.access_token,
            member_token: data.access_token,
          })
        );
        // Remember that THIS code was the one spent successfully, so a reload of
        // the same URL resumes instead of reporting the (correct, but useless)
        // "already used" — see the catch below.
        try {
          sessionStorage.setItem(spentKey(code), "1");
        } catch {
          /* private mode — the reload just shows the error instead */
        }
        navigate(`/membership/${token}/${START_PAGE}`, { replace: true });
      } catch (err) {
        // Reloading or going Back re-runs this effect against a code the first
        // run already spent, so the API rightly says it is gone. Sending the
        // creator to an error page for succeeding twice is wrong — resume the
        // session that redeem created. Keyed on the code itself, so a genuinely
        // dead link still reports the failure rather than silently reusing
        // whatever session happens to be in storage.
        let spent = false;
        try {
          spent = sessionStorage.getItem(spentKey(code)) === "1";
        } catch {
          /* ignore */
        }
        if (spent && localStorage.getItem(memberKey(token))) {
          navigate(`/membership/${token}/${START_PAGE}`, { replace: true });
          return;
        }
        setError(
          err?.response?.data?.message ||
            "This sign-in link has expired. Generate a new one from the admin."
        );
      }
    })();
  }, [code, token, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F6F7F9] dark:bg-canvas px-6">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-surface p-8 text-center shadow-sm">
        {error ? (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/15 text-red-500">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>
            <h1 className="text-base font-bold text-gray-900">Sign-in link not valid</h1>
            <p className="mt-1.5 text-sm text-gray-500">{error}</p>
            <button
              type="button"
              onClick={() => navigate(`/membership/${token}/login`, { replace: true })}
              className="mt-5 w-full rounded-xl bg-ink px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black"
            >
              Go to login
            </button>
          </>
        ) : (
          <>
            <span className="mx-auto mb-4 block h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200 border-t-gray-900" />
            <h1 className="text-base font-bold text-gray-900">Signing you in…</h1>
            <p className="mt-1.5 text-sm text-gray-500">Opening the membership.</p>
          </>
        )}
      </div>
    </div>
  );
}
